import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { KpiCard, RankedTable } from '../components';
import type { KpiPanel } from '../components';
import { useSummary } from '../hooks';
import { selectPeriod } from '../state/filtersSlice';
import type { Period, SalesBlock } from '../api/types';
import { formatMoney, moneyLabel, MONEY_UNIT } from '../utils/format';
import { PERIOD_LABELS, orderPair, pairLabel } from '../utils/periods';
import SuScreen from './home/SuScreen';
import StoreRankTable from './home/StoreRankTable';
import { SALES_TITLE, SECTION_SUFFIX } from './home/periodLabels';
import { STATE_CITIES_PATH } from './StateWisePage';
import { regionColumns } from './states/regionColumns';
import { useRegionBreakdown } from './stores/useSuFetch';
import './stores/stores.css';
import './states/states.css';

const HomePage: React.FC = () => {
  const history = useHistory();
  const period = useSelector((s: any) => selectPeriod(s)) as Period;
  const { data, loading, error, refetch } = useSummary();
  const lfl = data?.lfl ?? null;
  const net = data?.network;
  const shown = data?.period ?? period;
  const comparePeriod = data?.compare_period ?? null;
  const onToday = period === 'today';
  // Yesterday's sales sit under Today's YoY, so they are only needed on the Today tab.
  const yesterday = useSummary('yesterday', onToday);
  const states = useRegionBreakdown('state', period === 'wtd' ? 'mtd' : period);
  const busy = loading && !data;

  // Two periods side by side (Today | WTD, WTD | MTD); on the Today tab Today is the big one with a Yesterday line.
  const pairPanels = (b: SalesBlock | null | undefined, ydaySales?: number | null): KpiPanel[] | undefined =>
    b && comparePeriod
      ? orderPair(shown, comparePeriod).map((p) => {
          const main = p === shown;
          const panel: KpiPanel = main
            ? { label: PERIOD_LABELS[p], sales: b.sales, lySales: b.ly_sales, yoyPct: b.yoy_pct }
            : { label: PERIOD_LABELS[p], sales: b.compare_sales, lySales: b.compare_ly_sales, yoyPct: b.compare_yoy_pct };
          if (p === 'today') {
            panel.big = true;
            panel.note = `Yesterday: ${formatMoney(ydaySales)} ${MONEY_UNIT.key}`;
          }
          return panel;
        })
      : undefined;
  const netPanels = pairPanels(net, yesterday.data?.network.sales);
  const lflPanels = pairPanels(lfl, yesterday.data?.lfl?.sales);
  const suffix = comparePeriod ? pairLabel(shown, comparePeriod) : SECTION_SUFFIX[period];

  const stateTable = useMemo(
    () => regionColumns('state', states.data?.period ?? period, states.data?.compare_period ?? null),
    [states.data?.period, states.data?.compare_period, period],
  );

  return (
    <SuScreen storeCount={net?.store_count} onRefresh={() => { refetch(); yesterday.refetch(); states.refetch(); }}
              error={error} hasData={!!data} asOf={data?.as_of}>
      <KpiCard
        title={`${comparePeriod ? `${pairLabel(shown, comparePeriod)} Sales` : SALES_TITLE[period]} (${moneyLabel()})`}
        panels={netPanels}
        sales={net?.sales}
        lySales={net?.ly_sales}
        yoyPct={net?.yoy_pct}
        storeCount={net?.store_count}
        loading={busy}
      />
      <KpiCard
        title={`${comparePeriod ? `LFL ${pairLabel(shown, comparePeriod)} Sales` : 'LFL Sales'} (${moneyLabel()})`}
        panels={lflPanels}
        sales={lfl?.sales}
        lySales={lfl?.ly_sales}
        yoyPct={lfl?.yoy_pct}
        storeCount={lfl?.store_count}
        unavailable={!!data && !lfl}
        loading={busy}
      />
      <div className="su-states-table dual">
        <RankedTable
          title="State Wise Performance"
          columns={stateTable.columns}
          headerGroups={stateTable.groups}
          rows={states.data?.items ?? null}
          rowKey={(r) => `${r.state}:${r.key}`}
          loading={states.loading}
          skeletonRows={6}
          emptyText="No data"
          onRowClick={(r) => history.push(`${STATE_CITIES_PATH}?state=${encodeURIComponent(r.key)}`)}
          onViewAll={() => history.push('/app/states')}
          viewAllLabel="View All >"
        />
      </div>
      <StoreRankTable
        title={`Top 5 Stores ${suffix}`}
        rows={data?.top_stores?.slice(0, 5)}
        period={shown}
        comparePeriod={comparePeriod}
        viewAllPath="/app/stores?sort=sales&order=desc"
        loading={busy}
      />
      <StoreRankTable
        title={`Bottom 5 Stores ${suffix}`}
        rows={data?.bottom_stores?.slice(0, 5)}
        period={shown}
        comparePeriod={comparePeriod}
        viewAllPath="/app/stores?sort=sales&order=asc"
        loading={busy}
      />
    </SuScreen>
  );
};

export default HomePage;
